import React from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Alert,
  Grid,
  Pagination,
  Button,
  Chip,
} from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import ProductImage from "../Product/Utils/ProductImage";

export default function WishlistUI({
  likes = [],
  loading = false,
  error = null,
  page = 1,
  totalPages = 1,
  onPageChange,
  onRemoveLike,
}) {
  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
        gap={2}
      >
        <CircularProgress color="primary" />
        <Typography variant="body2" color="text.secondary">
          در حال دریافت علاقمندی‌ها...
        </Typography>
      </Box>
    );
  }

  const safeLikes = Array.isArray(likes) ? likes : [];

  return (
    <Box dir="rtl" sx={{ bgcolor: "#f7f7f7", py: { xs: 2.5, md: 4 }, minHeight: "100vh" }}>
      <Container maxWidth="xl">
        {/* Header Section */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={{ xs: 2.5, md: 3 }}
        >
          <Box display="flex" alignItems="center" gap={1.25}>
            <FavoriteIcon color="error" sx={{ fontSize: { xs: 28, md: 34 } }} />
            <Typography variant="h5" fontWeight={800} fontSize={{ xs: "1.25rem", md: "1.5rem" }}>
              لیست علاقمندی‌های من
            </Typography>
          </Box>
          {safeLikes.length > 0 && (
            <Chip
              label={`${safeLikes.length} محصول`}
              color="primary"
              variant="outlined"
              size="small"
              sx={{ fontWeight: 700 }}
            />
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
            {error}
          </Alert>
        )}

        {/* Empty State */}
        {!error && safeLikes.length === 0 ? (
          <Card
            sx={{
              p: { xs: 4, md: 6 },
              textAlign: "center",
              borderRadius: 4,
              boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
              maxWidth: 500,
              mx: "auto",
              mt: 4,
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                bgcolor: "#fee2e2",
                color: "#ef4444",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <FavoriteBorderIcon sx={{ fontSize: 36 }} />
            </Box>
            <Typography variant="h6" fontWeight={700} mb={1}>
              لیست علاقمندی‌های شما خالی است
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              محصولات مورد علاقه خود را اضافه کنید تا بعداً راحت‌تر به آن‌ها دسترسی داشته باشید.
            </Typography>
            <Button
              component={Link}
              to="/"
              variant="contained"
              disableElevation
              startIcon={<ShoppingBagIcon sx={{ ml: 1, mr: -0.5 }} />}
              sx={{
                borderRadius: 2.5,
                px: 3,
                py: 1,
                fontWeight: 700,
              }}
            >
              مشاهده محصولات
            </Button>
          </Card>
        ) : (
          <>
            {/* Grid Container */}
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              {safeLikes.map((item) => {
                const product = item?.product || {};
                const variants = product.variants || [];

                const validPrices = variants
                  .map((v) => Number(v.price))
                  .filter((p) => !isNaN(p) && p > 0);

                const rawPrice =
                  validPrices.length > 0
                    ? Math.min(...validPrices)
                    : product.min_price || product.price;

                const hasPrice = rawPrice !== null && rawPrice !== undefined && rawPrice !== "";
                // Backend sends prices in toman — display as-is.
                const priceInToman = hasPrice ? Math.floor(Number(rawPrice)) : null;

                return (
                  <Grid
                    size={{ xs: 6, sm: 4, md: 3, lg: 2.4 }}
                    key={product.id || item.id}
                  >
                    <Card
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 3,
                        position: "relative",
                        bgcolor: "#fff",
                        border: "1px solid",
                        borderColor: "grey.200",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                        transition: "transform 0.2s ease, box-shadow 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                        },
                      }}
                    >
                      {/* Remove Like Heart Button */}
                      <IconButton
                        onClick={(e) => onRemoveLike && onRemoveLike(e, product.id)}
                        size="small"
                        aria-label="حذف از علاقمندی‌ها"
                        sx={{
                          position: "absolute",
                          top: 8,
                          left: 8,
                          zIndex: 2,
                          bgcolor: "rgba(255,255,255,0.9)",
                          backdropFilter: "blur(4px)",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                          WebkitTapHighlightColor: "transparent",
                          "&:hover": {
                            bgcolor: "#fee2e2",
                            transform: "scale(1.1)",
                          },
                          transition: "all 0.2s ease",
                        }}
                      >
                        <FavoriteIcon color="error" fontSize="small" />
                      </IconButton>

                      <Box
                        component={Link}
                        to={`/product/${product.slug}`}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          height: "100%",
                          textDecoration: "none",
                          color: "inherit",
                        }}
                      >
                        <ProductImage
                          images={product.images || []}
                          title={product.title}
                        />

                        <CardContent
                          sx={{
                            flexGrow: 1,
                            display: "flex",
                            flexDirection: "column",
                            p: { xs: 1.25, sm: 2 },
                            "&:last-child": { pb: { xs: 1.25, sm: 2 } },
                          }}
                        >
                          <Typography
                            sx={{
                              height: "2.8em",
                              lineHeight: 1.4,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              fontWeight: 600,
                              fontSize: { xs: "0.825rem", sm: "0.9rem" },
                              mb: 1,
                            }}
                          >
                            {product.title || "نامشخص"}
                          </Typography>

                          <Box
                            sx={{
                              mt: "auto",
                              pt: 1.25,
                              borderTop: "1px solid",
                              borderColor: "divider",
                              display: "flex",
                              justifyContent: "flex-end",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            {hasPrice ? (
                              <>
                                <Typography
                                  fontWeight={800}
                                  color="primary.main"
                                  fontSize={{ xs: "0.9rem", sm: "1.05rem" }}
                                >
                                  {priceInToman.toLocaleString("en-US")}
                                </Typography>
                                <Typography
                                  component="span"
                                  color="text.secondary"
                                  fontSize={{ xs: "0.7rem", sm: "0.75rem" }}
                                  fontWeight={400}
                                >
                                  تومان
                                </Typography>
                              </>
                            ) : (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={600}
                              >
                                ناموجود
                              </Typography>
                            )}
                          </Box>
                        </CardContent>
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <Box sx={{ mt: 4, mb: 1, display: "flex", justifyContent: "center" }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={onPageChange}
                  color="primary"
                  shape="rounded"
                  sx={{ direction: "ltr" }}
                  size="small"
                />
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
}